// alignmentStats.ts
// Turning counts into the rows RoleCoverage / ClassCoverage / CoverageSidebar
// draw. Spec: md/CLASS_ALIGNMENT_PLAN.md
//
// ONE SHAPING PATH, two sources, because the officer page and the player pages
// must never disagree about how many healers the guild has:
//
//   officers  hold the whole people list already, so they tally it in the
//             browser with tallyPeople and no extra request.
//   players   are never sent the people list. The server counts for them in
//             auth/alignment_overview.js (buildPoolStats) and sends the same
//             flat maps back.
//
// Both then go through buildCoverage, so the joining to the catalogue, the
// colours, the icons, the sort order and the dropping of empty specs happen in
// exactly one place.
//
// THE COUNTING RULE, which is the same one auth/alignment_overview.js explains
// at length: "can" is once per PERSON who listed the thing, so it overlaps
// across roles and classes and does NOT sum to the roster. "wants" is their
// rank 1 only, so it is disjoint and does sum.
import { getZamClassIconUrl } from "./modalChrome";
import { ROLE_COLOR } from "./alignmentUi";

export type StatItem = { can: number; wants: number };
// Keyed on catalogue item_key. Missing key means nobody picked it.
export type CountMaps = {
  roles: Record<string, StatItem>;
  classes: Record<string, StatItem>;
  specs: Record<string, StatItem>;
};
export type PoolStats = CountMaps & {
  registered: number;
  factions: Record<string, number>;
  realmTypes: Record<string, number>;
};

type CatalogItem = {
  item_key: string; label: string; parent_key: string | null;
  role_key: string | null; color: string | null; image_url?: string | null;
};
type CatalogLike = { classes: CatalogItem[]; specs: CatalogItem[]; roles: CatalogItem[] };

export type RoleRow = {
  key: string; label: string; color: string; can: number; wants: number;
};
export type SpecRow = RoleRow & { icon: string | null };
export type ClassRow = RoleRow & { icon: string | null; specs: SpecRow[] };

const EMPTY: StatItem = { can: 0, wants: 0 };

// Officer side: the same tally the server does, over the people list the
// officer page already has. A person is a Set member per key, never a count,
// so two warrior specs on one player is still one warrior.
export function tallyPeople(
  people: { id: string; combos: { class_key: string; spec_key: string; rank: number }[] }[],
  roleOfSpec: (specKey: string) => string | null | undefined
): CountMaps {
  const can: Record<keyof CountMaps, Map<string, Set<string>>> = {
    roles: new Map(), classes: new Map(), specs: new Map(),
  };
  const wants: Record<keyof CountMaps, Map<string, number>> = {
    roles: new Map(), classes: new Map(), specs: new Map(),
  };
  const addCan = (b: keyof CountMaps, key: string | null | undefined, personId: string) => {
    if (!key) return;
    if (!can[b].has(key)) can[b].set(key, new Set());
    can[b].get(key)!.add(personId);
  };
  const addWant = (b: keyof CountMaps, key: string | null | undefined) => {
    if (!key) return;
    wants[b].set(key, (wants[b].get(key) || 0) + 1);
  };

  people.forEach((p) => {
    p.combos.forEach((c) => {
      const role = roleOfSpec(c.spec_key);
      addCan("roles", role, p.id);
      addCan("classes", c.class_key, p.id);
      addCan("specs", c.spec_key, p.id);
      if (c.rank === 1) {
        addWant("roles", role);
        addWant("classes", c.class_key);
        addWant("specs", c.spec_key);
      }
    });
  });

  const pack = (b: keyof CountMaps) => {
    const out: Record<string, StatItem> = {};
    can[b].forEach((set, key) => { out[key] = { can: set.size, wants: 0 }; });
    wants[b].forEach((n, key) => {
      if (!out[key]) out[key] = { can: 0, wants: 0 };
      out[key].wants = n;
    });
    return out;
  };
  return { roles: pack("roles"), classes: pack("classes"), specs: pack("specs") };
}

// Roles keep the catalogue's own order, because tank, healer, melee, ranged is
// how every officer already reads a composition and sorting it by size would
// reshuffle the list every time somebody signs up.
export function buildRoles(catalog: CatalogLike | null, counts: CountMaps): RoleRow[] {
  return (catalog?.roles || []).map((r) => {
    const n = counts.roles[r.item_key] || EMPTY;
    return {
      key: r.item_key,
      label: r.label,
      color: r.color || ROLE_COLOR[r.item_key] || "#94a3b8",
      can: n.can,
      wants: n.wants,
    };
  });
}

// Classes ARE sorted by depth, because "what do we have a lot of" is the
// question, and unlike roles the list is long enough that scanning it in
// catalogue order answers nothing.
//
// SPEC COLOUR IS THE ROLE'S, NOT THE CLASS'S, even though alignment_catalog
// stores the class colour on spec rows: inside a class the class is already
// named, so the useful thing to see is which of those druids would heal.
export function buildClasses(catalog: CatalogLike | null, counts: CountMaps): ClassRow[] {
  return (catalog?.classes || [])
    .map((cl) => {
      const n = counts.classes[cl.item_key] || EMPTY;
      const fallbackIcon = cl.image_url || getZamClassIconUrl(cl.label);
      return {
        key: cl.item_key,
        label: cl.label,
        color: cl.color || "#94a3b8",
        icon: fallbackIcon,
        can: n.can,
        wants: n.wants,
        // Only specs somebody actually listed. A card full of zeroes hides the
        // one line on it that matters.
        specs: (catalog?.specs || [])
          .filter((s) => s.parent_key === cl.item_key)
          .map((s) => {
            const sn = counts.specs[s.item_key] || EMPTY;
            return {
              key: s.item_key,
              label: s.label,
              color: ROLE_COLOR[s.role_key || ""] || "#94a3b8",
              icon: s.image_url || fallbackIcon,
              can: sn.can,
              wants: sn.wants,
            };
          })
          .filter((s) => s.can > 0)
          .sort((a, b) => b.can - a.can || a.label.localeCompare(b.label)),
      };
    })
    .sort((a, b) => b.can - a.can || a.label.localeCompare(b.label));
}
